import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { UseQueryOptions, UseMutationOptions } from "@tanstack/react-query";
import { customFetch } from "./custom-fetch";
import type { ErrorType } from "./custom-fetch";

export interface Todo {
  id: number;
  label: string;
  completed: boolean;
  deadline?: string | null;
  createdAt: string;
}

export const TODOS_QUERY_KEY = ["todos"] as const;

export const getTodos = async (): Promise<Todo[]> => {
  return customFetch<Todo[]>("/api/todos");
};

export const useGetTodos = <TData = Todo[], TError = ErrorType<unknown>>(
  options?: UseQueryOptions<Todo[], TError, TData>
) => {
  return useQuery<Todo[], TError, TData>({
    queryKey: TODOS_QUERY_KEY,
    queryFn: getTodos,
    ...options,
  });
};

export const createTodo = async (label: string, deadline?: string | null): Promise<Todo> => {
  return customFetch<Todo>("/api/todos", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ label, deadline: deadline ?? null }),
  });
};

export const useCreateTodo = <TError = ErrorType<unknown>, TContext = unknown>(
  options?: UseMutationOptions<Todo, TError, { label: string; deadline?: string | null }, TContext>
) => {
  const queryClient = useQueryClient();
  return useMutation<Todo, TError, { label: string; deadline?: string | null }, TContext>({
    mutationFn: ({ label, deadline }) => createTodo(label, deadline),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TODOS_QUERY_KEY });
    },
    ...options,
  });
};

export const updateTodo = async (
  id: number,
  data: { label?: string; completed?: boolean; deadline?: string | null }
): Promise<Todo> => {
  return customFetch<Todo>(`/api/todos/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
};

export const useUpdateTodo = <TError = ErrorType<unknown>, TContext = unknown>(
  options?: UseMutationOptions<
    Todo,
    TError,
    { id: number; label?: string; completed?: boolean; deadline?: string | null },
    TContext
  >
) => {
  const queryClient = useQueryClient();
  return useMutation<
    Todo,
    TError,
    { id: number; label?: string; completed?: boolean; deadline?: string | null },
    TContext
  >({
    mutationFn: ({ id, ...data }) => updateTodo(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TODOS_QUERY_KEY });
    },
    ...options,
  });
};

export const deleteTodo = async (id: number): Promise<void> => {
  return customFetch<void>(`/api/todos/${id}`, {
    method: "DELETE",
  });
};

export const useDeleteTodo = <TError = ErrorType<unknown>, TContext = unknown>(
  options?: UseMutationOptions<void, TError, number, TContext>
) => {
  const queryClient = useQueryClient();
  return useMutation<void, TError, number, TContext>({
    mutationFn: (id: number) => deleteTodo(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TODOS_QUERY_KEY });
    },
    ...options,
  });
};
